import { useState, useEffect, useCallback } from 'react';
import { message } from '../utils/antdMessage';
import apiFetch from '../utils/apiFetch';

/**
 * 活跃流列表 + 队员电量/上报时间 + 自动刷新。
 */
export default function useMultiWindowDevices({
  autoRefresh,
  refreshInterval
}) {
  const [availableStreams, setAvailableStreams] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  /** streamKey / 队员名 → 电量、上报时间（全屏信息条） */
  const [memberMetaByKey, setMemberMetaByKey] = useState({});

  // 获取活跃流列表
  const fetchActiveStreams = async () => {
    try {
      // 使用与指挥系统页面相同的直接fetch调用方式
      const token = localStorage.getItem('token');
      const response = await fetch('/api/streaming/active-streams', {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      const data = await response.json();

      if (data.success) {
        setAvailableStreams(data.activeStreams);
        // 移除自动添加流的逻辑，只更新可用流列表
      }
    } catch (error) {
      console.error('获取活跃流失败:', error);
      message.error('获取活跃流失败');
    } finally {
      setIsLoading(false);
    }
  };

  /** 拉取队员电量/上报时间，按流密钥与姓名建索引 */
  const fetchMemberMeta = useCallback(async () => {
    try {
      const response = await apiFetch('/api/device/list?display_days=7');
      const data = await response.json();
      if (!data.success) return;
      const devices = data.devices || [];
      const map = {};
      devices.forEach((d) => {
        const name = d.name || '';
        const key = name.replace(/\s+/g, '');
        const meta = {
          batteryLevel: d.battery_level,
          lastSeen: d.last_seen,
          name
        };
        if (key) map[key] = meta;
        if (name) map[name] = meta;
        if (d.device_unique_id) map[d.device_unique_id] = meta;
        // Prefer resolving SRS streamKey → 队员名 (bound stream_key / push_code)
        const sk = String(d.stream_key || d.streamKey || d.push_code || '').trim();
        if (sk) map[sk] = meta;
        const tok = String(d.bind_token || d.token || d.tracker_token || '').trim();
        if (tok) map[tok] = meta;
      });
      setMemberMetaByKey(map);
    } catch (error) {
      console.error('获取队员电量信息失败:', error);
    }
  }, []);

  // 自动刷新：活跃流 + 队员电量
  useEffect(() => {
    fetchActiveStreams();
    fetchMemberMeta();

    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchActiveStreams();
        fetchMemberMeta();
      }, refreshInterval * 1000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval, fetchMemberMeta]);

  return {
    availableStreams,
    setAvailableStreams,
    isLoading,
    memberMetaByKey,
    fetchActiveStreams,
    fetchMemberMeta
  };
}
